/**
 * Import Context Flow
 *
 * Source brands → Context Bank diagram with a traveling beam
 * (inspired by Magic UI / GodUI animated-beam patterns).
 */

'use client'

import { useEffect, useId, useRef, useState, type ComponentType } from 'react'
import {
  IconBrandGoogle,
  IconBrandOpenai,
  IconDatabase,
  IconMessage
} from '@tabler/icons-react'
import { motion } from 'motion/react'

import { cn } from '@/lib/utils'

const sources: Array<{
  name: string
  icon: ComponentType<{ className?: string; stroke?: number }>
}> = [
  { name: 'ChatGPT', icon: IconBrandOpenai },
  { name: 'Claude', icon: IconMessage },
  { name: 'Gemini', icon: IconBrandGoogle }
]

export function ImportContextFlow({ className }: { className?: string }) {
  const id = useId().replace(/:/g, '')
  const containerRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const sourceRefs = useRef<Array<HTMLDivElement | null>>([])
  const [paths, setPaths] = useState<string[]>([])

  useEffect(() => {
    const update = () => {
      const container = containerRef.current
      const target = targetRef.current
      if (!container || !target) return

      const c = container.getBoundingClientRect()
      const t = target.getBoundingClientRect()
      const tx = t.left - c.left + t.width / 2
      const ty = t.top - c.top

      const next = sourceRefs.current.map(node => {
        if (!node) return ''
        const s = node.getBoundingClientRect()
        const sx = s.left - c.left + s.width / 2
        const sy = s.top - c.top + s.height
        const midY = (sy + ty) / 2
        return `M ${sx} ${sy} C ${sx} ${midY}, ${tx} ${midY}, ${tx} ${ty}`
      })

      setPaths(next.filter(Boolean))
    }

    update()
    const observer = new ResizeObserver(update)
    if (containerRef.current) observer.observe(containerRef.current)
    window.addEventListener('resize', update)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={cn('relative flex flex-col items-stretch gap-10 py-2', className)}
    >
      <div className="relative z-10 flex flex-wrap items-center justify-center gap-3 sm:justify-start">
        {sources.map(({ name, icon: Icon }, index) => (
          <div
            key={name}
            ref={node => {
              sourceRefs.current[index] = node
            }}
            className="inline-flex items-center gap-2 border border-slate-200/90 bg-white px-3 py-2 text-sm font-medium text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
          >
            <Icon className="size-4" stroke={1.5} />
            {name}
          </div>
        ))}
      </div>

      <svg
        aria-hidden
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      >
        <defs>
          <linearGradient id={`beam-grad-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgb(100, 116, 139)" stopOpacity="0" />
            <stop offset="40%" stopColor="rgb(71, 85, 105)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="rgb(15, 23, 42)" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        {paths.map((d, index) => (
          <g key={index}>
            <path
              d={d}
              fill="none"
              stroke={`url(#beam-grad-${id})`}
              strokeWidth="1.5"
              strokeOpacity="0.35"
            />
            <motion.path
              d={d}
              fill="none"
              stroke="rgb(71, 85, 105)"
              strokeWidth="2"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: [0, 1], opacity: [0, 0.85, 0] }}
              transition={{
                duration: 2.4,
                delay: index * 0.35,
                repeat: Infinity,
                ease: 'easeInOut',
                repeatDelay: 0.6
              }}
              className="dark:stroke-sky-300/70"
            />
          </g>
        ))}
      </svg>

      <div className="relative z-10 flex justify-center sm:justify-start">
        <div
          ref={targetRef}
          className="inline-flex items-center gap-2 border border-slate-950 bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
        >
          <IconDatabase className="size-4" stroke={1.5} />
          Losi Context Bank
        </div>
      </div>
    </div>
  )
}
